/**
 * REST-Compliant Bot API Service
 *
 * REST-style bot API: sessions, messages, config, streaming. Uses gateway/public
 * base URL (no APIM). No legacy fallbacks.
 */

import { logger } from 'utils/logger';

class RestBotApiService {
  constructor(baseUrl, options = {}) {
    this.baseUrl = baseUrl || (typeof window !== 'undefined' && window._env_?.API_BASE_URL) || process.env.REACT_APP_API_BASE_URL;
    this.timeout = options.timeout || 30000;
    this.retryCount = options.retryCount || 3;

    this.sessionCache = new Map();
    this.configCache = new Map();
  }

  async createRequest(url, options = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    const config = {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers
      },
      ...options
    };
    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  async handleResponse(response, expectedStatus = 200) {
    if (response.status === expectedStatus) {
      return response.json();
    }
    switch (response.status) {
      case 400:
        throw new ApiError('Bad Request - Invalid request data', response.status);
      case 401:
        throw new ApiError('Unauthorized - Authentication required', response.status);
      case 403:
        throw new ApiError('Forbidden - Access denied', response.status);
      case 404:
        throw new ApiError('Not Found - Resource not found', response.status);
      case 409:
        throw new ApiError('Conflict - Resource conflict', response.status);
      case 422:
        throw new ApiError('Unprocessable Entity - Validation failed', response.status);
      case 429:
        throw new ApiError('Too Many Requests - Rate limit exceeded', response.status);
      case 500:
        throw new ApiError('Internal Server Error', response.status);
      default:
        throw new ApiError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }
  }

  async retryRequest(requestFn, retries = this.retryCount) {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        if (attempt === retries || error.status < 500) throw error;
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  async createSession(botId, implementationType = 'widget') {
    const cacheKey = `${botId}-${implementationType}`;
    if (this.sessionCache.has(cacheKey)) {
      const cached = this.sessionCache.get(cacheKey);
      if (Date.now() - cached.timestamp < 300000) return cached.data;
    }
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/sessions`, {
        method: 'POST',
        body: JSON.stringify({ bot_id: botId, implementation_type: implementationType })
      });
      const result = await this.handleResponse(response, 201);
      this.sessionCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    };
    return this.retryRequest(requestFn);
  }

  async getSession(sessionId) {
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/sessions/${sessionId}`, { method: 'GET' });
      return this.handleResponse(response, 200);
    };
    return this.retryRequest(requestFn);
  }

  async deleteSession(sessionId) {
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/sessions/${sessionId}`, { method: 'DELETE' });
      if (response.status === 204) {
        this.sessionCache.forEach((value, key) => {
          if (value.data.data?.session_id === sessionId) this.sessionCache.delete(key);
        });
        return { success: true };
      }
      return this.handleResponse(response, 204);
    };
    return this.retryRequest(requestFn);
  }

  async generateResponse(sessionId, message, botId, context = {}) {
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message, bot_id: botId, context })
      });
      return this.handleResponse(response, 201);
    };
    return this.retryRequest(requestFn);
  }

  async getSessionMessages(sessionId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.before) params.append('before', options.before);
    if (options.after) params.append('after', options.after);
    const queryString = params.toString();
    const url = `${this.baseUrl}/sessions/${sessionId}/messages${queryString ? '?' + queryString : ''}`;
    const requestFn = async () => {
      const response = await this.createRequest(url, { method: 'GET' });
      return this.handleResponse(response, 200);
    };
    return this.retryRequest(requestFn);
  }

  async streamChatResponse(sessionId, message, botId, onMessage, onError, onComplete) {
    try {
      const response = await this.createRequest(`${this.baseUrl}/sessions/${sessionId}/stream`, {
        method: 'POST',
        body: JSON.stringify({ message, bot_id: botId }),
        headers: { 'Accept': 'text/event-stream', 'Cache-Control': 'no-cache' }
      });
      if (response.status !== 200) {
        await this.handleResponse(response, 200);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          onComplete && onComplete();
          break;
        }
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              onMessage && onMessage(data);
            } catch (err) {
              logger.warn('BOT-API-REST', 'Failed to parse SSE data', { error: err.message });
            }
          }
        }
      }
    } catch (error) {
      onError && onError(error);
      throw error;
    }
  }

  async deleteConversation(conversationId) {
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/conversations/${conversationId}`, { method: 'DELETE' });
      if (response.status === 204) return { success: true };
      return this.handleResponse(response, 204);
    };
    return this.retryRequest(requestFn);
  }

  async getBotConfig(botId, credentials) {
    const cacheKey = `config-${botId}`;
    if (this.configCache.has(cacheKey)) {
      const cached = this.configCache.get(cacheKey);
      if (Date.now() - cached.timestamp < 600000) return cached.data;
    }
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/bots/${botId}/config`, {
        method: 'GET',
        headers: { 'Authorization': `Basic ${btoa(`${credentials.managementId}:${credentials.managementSecret}`)}` }
      });
      const result = await this.handleResponse(response, 200);
      this.configCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    };
    return this.retryRequest(requestFn);
  }

  async updateBotConfig(botId, config, credentials) {
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/bots/${botId}/config`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${btoa(`${credentials.managementId}:${credentials.managementSecret}`)}` },
        body: JSON.stringify(config)
      });
      const result = await this.handleResponse(response, 200);
      this.configCache.delete(`config-${botId}`);
      return result;
    };
    return this.retryRequest(requestFn);
  }

  async validateBotToken(botId, token, implementationType = 'widget') {
    const requestFn = async () => {
      const response = await this.createRequest(`${this.baseUrl}/bots/${botId}/validate`, {
        method: 'POST',
        body: JSON.stringify({ token, implementation_type: implementationType })
      });
      return this.handleResponse(response, 200);
    };
    return this.retryRequest(requestFn);
  }

  async getUsageStatistics(resourceId, limit = 10000) {
    const url = `${this.baseUrl}/api/v1/resources/${resourceId}/knowledge/usage/statistics?limit=${limit}`;
    const response = await this.createRequest(url, { method: 'GET' });
    return this.handleResponse(response, 200);
  }

  async getSourceUsage(resourceId, knowledgeId) {
    const url = `${this.baseUrl}/api/v1/resources/${resourceId}/knowledge/${knowledgeId}/usage`;
    const response = await this.createRequest(url, { method: 'GET' });
    return this.handleResponse(response, 200);
  }

  async getChunkUsage(resourceId, chunkId) {
    const url = `${this.baseUrl}/api/v1/resources/${resourceId}/knowledge/chunks/${chunkId}/usage`;
    const response = await this.createRequest(url, { method: 'GET' });
    return this.handleResponse(response, 200);
  }

  clearCache() {
    this.sessionCache.clear();
    this.configCache.clear();
  }

  getCacheStats() {
    return {
      sessionCacheSize: this.sessionCache.size,
      configCacheSize: this.configCache.size,
      totalCachedItems: this.sessionCache.size + this.configCache.size
    };
  }
}

class ApiError extends Error {
  constructor(message, status, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
  toJSON() {
    return { name: this.name, message: this.message, status: this.status, details: this.details, timestamp: this.timestamp };
  }
}

export function createBotApiService(baseUrl, options = {}) {
  return new RestBotApiService(baseUrl, { timeout: 30000, retryCount: 3, ...options });
}

export { RestBotApiService, ApiError };
export default RestBotApiService;
